import type { ChartTone } from "../content/pinyin-chart/pinyin-chart.js";
import type { ChartPlayer, PlayStep } from "./pinyin-chart-player.js";
import {
  type PlaygroundWord,
  playgroundKeystroke,
  readPlayground,
  wordReading,
} from "./pinyin-chart-playground.js";
import { toneMarked } from "../site/fuxi-tone-numbers.js";

/** What the playground needs from the rest of the chart. */
export interface PlaygroundStand {
  readonly player: ChartPlayer;

  /** The recording of a chart tone, by key, where it has one. */
  readonly toneOf: (key: string) => ChartTone | undefined;

  /** A recording's file in the voice the reader chose. */
  readonly srcOf: (recording: string) => string;
}

/** Quiet between two words, the player's gap between two files. */
const wordGap = 350;

/**
 * Where a word's own recording would be, if the 词典 has one.
 *
 * A word's recordings are filed under its reading, `_pinyin/bei3jing1/`,
 * which the browser can write from what was typed. `wordReading` says which
 * words have one to look for, neutral tones included.
 */
function wholeWordRecording(word: PlaygroundWord): string | undefined {
  const reading = wordReading(word);

  return reading === undefined
    ? undefined
    : `/audio/cidian/_pinyin/${reading}/${reading}`;
}

/** Where each word's first button is, counting every syllable before it. */
function firsts(read: readonly PlaygroundWord[]): number[] {
  let count = 0;

  return read.map((word) => {
    const first = count;

    count += word.syllables.length;

    return first;
  });
}

/**
 * The Pinyin Playground under the chart: a field that plays what is typed.
 *
 * `./pinyin-chart-playground.ts` reads the field and says what to play. This
 * half writes digits as marks under the caret, draws a button for every
 * syllable typed, and plays. A syllable the chart has no recording of is
 * drawn and skipped, and its button says so.
 *
 * **A word plays its own recording where the 词典 has one.** 北京 recorded
 * as a word is said the way a speaker says it, which two syllables joined
 * are not. The playground asks for the recording at the word's reading and
 * plays its syllables where that fails, so there is no table of recordings
 * for a reader to download first.
 * @param container - The chart, which holds `[data-cb-pc="playground"]`.
 * @param stand - The player and the chart's recordings.
 */
export function startPlayground(
  container: HTMLElement,
  stand: PlaygroundStand,
): void {
  const section = container.querySelector<HTMLElement>(
    '[data-cb-pc="playground"]',
  );
  const field = section?.querySelector<HTMLInputElement>(
    "[data-cb-pc-playground-input]",
  );
  const words = section?.querySelector<HTMLElement>(
    "[data-cb-pc-playground-words]",
  );
  const playAll = section?.querySelector<HTMLButtonElement>(
    "[data-cb-pc-playground-play]",
  );

  if (
    section === null ||
    section === undefined ||
    field === null ||
    field === undefined ||
    words === null ||
    words === undefined
  ) {
    return;
  }

  const document = container.ownerDocument;
  let sounded: string | undefined;
  let buttons: HTMLButtonElement[] = [];

  /** A word as its syllables, one step each with no gap between them. */
  const syllableSteps = (
    word: PlaygroundWord,
    first: number,
    pauseBefore: number | undefined,
  ): PlayStep[] =>
    word.syllables.flatMap((syllable, at) => {
      const audio =
        syllable.key === undefined
          ? undefined
          : stand.toneOf(syllable.key)?.audio;
      const button = buttons[first + at];

      return audio === undefined
        ? []
        : [
            {
              src: stand.srcOf(audio),
              ...(at > 0
                ? { pauseBefore: 0 }
                : pauseBefore !== undefined && { pauseBefore }),
              onStart: (): void => {
                mark(button === undefined ? [] : [button]);
              },
            },
          ];
    });

  const mark = (playing: readonly HTMLButtonElement[]): void => {
    for (const button of buttons) {
      button.classList.toggle("is-playing", playing.includes(button));
    }
  };

  /**
   * Play words in turn, each as its own recording where the 词典 has one and
   * as its syllables where it has not. Anything played after stops the rest.
   */
  const playWords = async (
    wanted: readonly {
      readonly word: PlaygroundWord;
      readonly first: number;
    }[],
  ): Promise<void> => {
    for (const [at, { word, first }] of wanted.entries()) {
      const pauseBefore = at === 0 ? undefined : wordGap;
      const whole = wholeWordRecording(word);
      const playing = buttons.slice(first, first + word.syllables.length);
      const heard =
        whole === undefined
          ? "failed"
          : // oxlint-disable-next-line no-await-in-loop -- the words play in order
            await stand.player.play([
              {
                src: stand.srcOf(whole),
                ...(pauseBefore !== undefined && { pauseBefore }),
                onStart: (): void => {
                  mark(playing);
                },
              },
            ]);

      if (heard === "stopped") {
        return;
      }

      const steps =
        heard === "failed" ? syllableSteps(word, first, pauseBefore) : [];

      if (
        steps.length > 0 &&
        // oxlint-disable-next-line no-await-in-loop -- the words play in order
        (await stand.player.play(steps)) === "stopped"
      ) {
        return;
      }
    }

    mark([]);
  };

  const draw = (): readonly PlaygroundWord[] => {
    const read = readPlayground(field.value);

    words.replaceChildren();
    buttons = [];

    for (const word of read) {
      const group = document.createElement("span");

      group.className = "cb-pc-playground-word";

      if (word.syllables.length === 0) {
        group.classList.add("is-unread");
        group.textContent = word.text;
      }

      for (const syllable of word.syllables) {
        const button = document.createElement("button");
        const at = buttons.length;
        const text = document.createElement("span");
        const recorded =
          syllable.key !== undefined &&
          stand.toneOf(syllable.key)?.audio !== undefined;

        button.type = "button";
        button.className = "cb-pc-sound cb-pc-playground-syllable";
        button.lang = "zh-Latn-CN-pinyin";
        text.className = `py-tone-${String(syllable.tone === 0 ? 5 : syllable.tone)}`;
        text.textContent = syllable.text;
        button.append(text);

        if (!recorded) {
          button.disabled = true;
          button.classList.add("is-missing");
          button.title =
            syllable.tone === 0
              ? "Add a tone from 1 to 4 to hear this syllable."
              : "There’s no recording for this syllable and tone.";
        } else {
          button.addEventListener("click", () => {
            void playWords([
              {
                word: { text: syllable.text, syllables: [syllable] },
                first: at,
              },
            ]);
          });
        }

        group.append(button);
        buttons.push(button);
      }

      words.append(group);
    }

    if (playAll !== null && playAll !== undefined) {
      playAll.disabled = !buttons.some((button) => !button.disabled);
    }

    return read;
  };

  field.addEventListener("input", () => {
    const caret = field.selectionStart ?? field.value.length;
    const before = field.value.slice(0, caret);
    const after = field.value.slice(caret);
    const keystroke = playgroundKeystroke(before, sounded);
    const marked = toneMarked(before);

    if (marked !== before) {
      field.value = `${marked}${after}`;
      field.setSelectionRange(marked.length, marked.length);
    }

    const read = draw();

    if (keystroke === undefined) {
      return;
    }

    sounded = keystroke.through;

    // The word the syllable finished is the last one up to the caret.
    const upTo = readPlayground(keystroke.through);
    const index = upTo.length - 1;

    void playWords([{ word: keystroke.word, first: firsts(read)[index] ?? 0 }]);
  });

  playAll?.addEventListener("click", () => {
    const read = readPlayground(field.value);
    const starts = firsts(read);

    void playWords(read.map((word, at) => ({ word, first: starts[at] ?? 0 })));
  });

  draw();
}
